import { Component, For, JSXElement, Show, onCleanup, onMount } from 'solid-js';
import { DATA_THEME_ATTRIBUTE, DEFAULT_MODUS_THEME } from '../constants';
import { MODUS_TREE_MENU_STYLESHEET } from '../../mc-custom-css/mc-tree-menu.css';
import { injectInRenderContext } from '../util';
import Icon from '../Icon/Icon';
import type { IconSize } from '../Icon/icon-size';
import { TreeItemProps, TreeItemSize } from '../TreeItem/TreeItem';

export type TreeMenuSize = 'sm' | 'md' | 'lg';

type ModusTreeMenuSize = 'sm' | 'md' | 'lg';

const treeSizeToIconSize = (
  size: TreeItemSize | TreeMenuSize | undefined
): IconSize => {
  switch (size) {
    case 'sm':
      return 'icon-small';
    case 'lg':
      return 'icon-large';
    default:
      return 'icon-medium';
  }
};

const toModusTreeMenuSize = (
  size: TreeMenuSize | undefined
): ModusTreeMenuSize => {
  switch (size) {
    case 'sm':
      return 'sm';
    case 'lg':
      return 'lg';
    default:
      return 'md';
  }
};
export type TreeMenuSelectionMode = 'single' | 'multiple';
export type TreeMenuOrientation = 'horizontal' | 'vertical';

export type TreeMenuItem = TreeItemProps;

/**
 * Stable id for the container div rendered inside modus-wc-tree-menu. The host
 * (tc-modus-tree-menu) wrapper locates this element (scoped to its own shadow
 * root or light DOM) and portals React children into it directly, rather than
 * relying on <slot> projection — which is a no-op when the host has no shadow
 * root (shadowDom=false).
 */
export const TREE_MENU_ITEMS_CONTAINER_ID = 'tc-modus-tree-menu-items';

export interface TreeMenuProps {
  /** Data-driven items rendered directly by SolidJS — no portal needed. */
  items?: TreeMenuItem[];
  size?: TreeMenuSize;
  selectionMode?: TreeMenuSelectionMode;
  orientation?: TreeMenuOrientation;
  /** Renders as a nested submenu (`modus-wc-menu-dropdown`) toggled by the parent tree-item. */
  isSubMenu?: boolean;
  bordered?: boolean;
  ariaLabel?: string;
  customClass?: string;
  /** Optional React children portaled via `TREE_MENU_ITEMS_CONTAINER_ID` for complex use cases. */
  children?: JSXElement;
}

/**
 * modus-wc-tree-item (scoped / no shadow DOM) unconditionally blocks item
 * selection when composedPath()[0] is inside [slot="end"].  The end-icon click
 * handler stops the original event and re-fires it on .modus-wc-menu-item-
 * interactive so composedPath()[0] is that div itself — not inside [slot="end"]
 * — and handleItemSelect() proceeds as normal.
 */
type TreeMenuItemRowProps = TreeMenuItem & { menuSize?: TreeMenuSize };

const TreeMenuItemRow: Component<TreeMenuItemRowProps> = (
  props
): JSXElement => {
  const defaultIconSize = (): IconSize =>
    treeSizeToIconSize(props.size ?? props.menuSize);
  const startIconSize = (): IconSize =>
    props.startIconSize ?? defaultIconSize();
  const endIconSize = (): IconSize => props.endIconSize ?? defaultIconSize();

  const handleEndIconClick = (e: MouseEvent): void => {
    e.stopPropagation();
    if (props.onEndIconSelect) {
      props.onEndIconSelect();
      return;
    }
    const treeItemEl = (e.currentTarget as HTMLElement).closest(
      'modus-wc-tree-item'
    );
    const interactive = treeItemEl?.querySelector(
      '.modus-wc-menu-item-interactive'
    ) as HTMLElement | null;
    interactive?.click();
  };

  return (
    <>
      <modus-wc-tree-item
        label={props.label}
        value={props.value}
        sub-label={props.subLabel ?? ''}
        selected={props.selected ?? false}
        disabled={props.disabled ?? false}
        tooltip-content={props.tooltipContent ?? ''}
        tooltip-position={props.tooltipPosition ?? 'top'}
      >
        <Show when={props.startIcon}>
          <span slot="start">
            <Icon
              icon={props.startIcon as string}
              size={startIconSize()}
              color={props.iconColor}
            />
          </span>
        </Show>
        <Show when={props.endIcon}>
          <span slot="end" onClick={handleEndIconClick}>
            <Icon
              icon={props.endIcon as string}
              size={endIconSize()}
              color={props.iconColor}
            />
          </span>
        </Show>
      </modus-wc-tree-item>
      <Show when={props.dividerAfter}>
        <hr />
      </Show>
    </>
  );
};

const TreeMenu: Component<TreeMenuProps> = (props): JSXElement => {
  let treeMenuRef: HTMLElement | undefined;

  const getDataTheme = (): string =>
    (typeof document !== 'undefined' &&
      document.documentElement.getAttribute(DATA_THEME_ATTRIBUTE)) ||
    DEFAULT_MODUS_THEME;

  onMount(() => {
    let cancelled = false;
    customElements.whenDefined('modus-wc-tree-menu').then(() => {
      if (cancelled || !treeMenuRef) return;
      injectInRenderContext(
        treeMenuRef,
        'mc-tree-menu-style',
        MODUS_TREE_MENU_STYLESHEET
      );
    });
    onCleanup(() => {
      cancelled = true;
    });
  });

  return (
    <div data-theme={getDataTheme()} style={{ display: 'contents' }}>
      <modus-wc-tree-menu
        ref={(el: HTMLElement) => {
          treeMenuRef = el;
        }}
        size={toModusTreeMenuSize(props.size)}
        selection-mode={props.selectionMode ?? 'single'}
        orientation={props.orientation ?? 'vertical'}
        is-sub-menu={props.isSubMenu ?? false}
        bordered={props.bordered ?? false}
        aria-label={props.ariaLabel ?? 'Tree menu'}
        custom-class={props.customClass ?? ''}
      >
        <For each={props.items ?? []}>
          {(item) => <TreeMenuItemRow {...item} menuSize={props.size} />}
        </For>
        <div id={TREE_MENU_ITEMS_CONTAINER_ID} style={{ display: 'contents' }}>
          {props.children}
        </div>
      </modus-wc-tree-menu>
    </div>
  );
};

export default TreeMenu;
